import { useVisitedOffersContext } from '@cian/frontend-visited-offers-widget';
import { useDispatch, useSelector } from 'react-redux';
import * as React from 'react';

import {
  selectHasMore,
  selectPaginationStatus,
  selectRecommendations,
  selectRecommendationsStatus,
} from '../../selectors/recommendations';
import { EPaginationStatus, TOfferAnalyticsData, TProduct } from '../../types/recommendation';
import { getRecommendations } from '../../thunk/recommendations/getRecommendations';
import { getMoreRecommendations } from '../../thunk/recommendations/getMoreRecommendations';
import { trackRecommendationItemClick, trackRecommendationsView } from './tracking';

export const useRecommendations = () => {
  const { isOfferVisited: isVisited } = useVisitedOffersContext();

  const isOfferVisited = React.useCallback((id: number | null) => !!id && isVisited(id), [isVisited]);

  const dispatch = useDispatch();
  const recommendations = useSelector(selectRecommendations);
  const hasMore = useSelector(selectHasMore);
  const recommendationsStatus = useSelector(selectRecommendationsStatus);
  const paginationStatus = useSelector(selectPaginationStatus);

  const onRecommendationClick = React.useCallback(
    (analyticsData: TOfferAnalyticsData) => {
      trackRecommendationItemClick(analyticsData.tag, analyticsData.products);
    },
    [trackRecommendationItemClick],
  );

  const onShow = React.useCallback(() => {
    const products = recommendations
      .map(({ offerId: id }, index) => ({
        id,
        position: index + 1,
        offerType: 'offer',
      }))
      .filter(({ id }) => id);

    const modelVersion = (recommendations[0].analyticsData as TOfferAnalyticsData).tag;

    trackRecommendationsView(modelVersion, products as TProduct[]);
  }, [recommendations, isOfferVisited]);

  const onLoad = React.useCallback(() => {
    dispatch(getRecommendations());
  }, [dispatch]);

  const onLoadMore = React.useCallback(() => {
    if (!hasMore || paginationStatus === EPaginationStatus.Loading) {
      return;
    }

    dispatch(getMoreRecommendations());
  }, [dispatch, hasMore, paginationStatus]);

  const onRetry = React.useCallback(() => {
    if (paginationStatus !== EPaginationStatus.Error) {
      return;
    }

    dispatch(getMoreRecommendations());
  }, [dispatch, paginationStatus]);

  return React.useMemo(
    () => ({
      recommendations,
      hasMore,
      recommendationsStatus,
      paginationStatus,
      onLoad,
      onLoadMore,
      onRetry,
      onRecommendationClick,
      onShow,
      isOfferVisited,
    }),
    [
      recommendations,
      hasMore,
      recommendationsStatus,
      paginationStatus,
      onLoad,
      onLoadMore,
      onRetry,
      onRecommendationClick,
      onShow,
      isOfferVisited,
    ],
  );
};
